// src/components/Auth/SessionManager.js
import { useEffect, useRef } from "react";
import {
  getAuthToken,
  isSessionExpired,
  updateActivityTimestamp,
  logoutUser,
} from "../../api/authSession";

/**
 * SessionManager Component
 * Invisibility monitors user interaction and automatically logs out after 30 minutes of idle time.
 */
const SessionManager = () => {
  const lastThrottleRef = useRef(Date.now());

  useEffect(() => {
    // Only activate listener if user is logged in
    if (!getAuthToken()) return;

    // Check immediately on mount/tab reactivation
    if (isSessionExpired()) {
      logoutUser("Your session has timed out due to 30 minutes of inactivity.");
      return;
    }

    // Handler to update activity with a 2-second throttle to save performance
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastThrottleRef.current > 2000) {
        lastThrottleRef.current = now;
        updateActivityTimestamp();
      }
    };

    // Events to monitor for activity
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });

    // Interval to check every 15 seconds if 30 minutes have elapsed
    const checkInterval = setInterval(() => {
      if (getAuthToken() && isSessionExpired()) {
        logoutUser("Your session has timed out due to 30 minutes of inactivity.");
      }
    }, 15000);

    // Also check when tab gains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (getAuthToken() && isSessionExpired()) {
          logoutUser("Your session has timed out due to 30 minutes of inactivity.");
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(checkInterval);
    };
  }, []);

  return null; // Headless component
};

export default SessionManager;
